"use client"

import * as React from "react"
import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { cn } from "@/lib/utils"
import { ThemeToggle } from "@/components/theme-toggle"
import { Button } from "@/components/ui/button"
import { WorkspaceSwitcher } from "@/components/workspace/workspace-switcher"
import { getFreshnessLabel } from "@/lib/data-freshness"

// ============================================================================
// TypeScript Types
// ============================================================================

/**
 * Navigation link configuration
 */
interface NavLink {
  href: string
  label: string
  children?: NavLink[]
}

/**
 * User data structure
 */
interface User {
  id: string
  email: string
  name: string
  isDefaultWorkspaceAdmin: boolean
  isWorkspaceAdmin: boolean
}

/**
 * Build navigation links for the application.
 * The D&D Gewinn entry is only shown to default workspace admins.
 */
function buildNavLinks(isDefaultWorkspaceAdmin: boolean): NavLink[] {
  return [
    { href: "/", label: "Dashboard" },
    { href: "/at-risk", label: "At Risk" },
    { href: "/shipments", label: "Shipments" },
    { href: "/vessels", label: "Vessels" },
    { href: "/demurrage", label: "Demurrage" },
    { href: "/detention", label: "Detention" },
    { href: "/combined", label: "Combined" },
    { href: "/storage", label: "Storage" },
    {
      href: "/analytics",
      label: "Analytics",
      children: [
        { href: "/analytics/carriers", label: "Carrier Analytics" },
        { href: "/analytics/containers", label: "Container Details" },
        { href: "/routes", label: "Route Analytics" },
        { href: "/dd-costs", label: "D&D Costs" },
        ...(isDefaultWorkspaceAdmin
          ? [{ href: "/analytics/dd-profit", label: "D&D Gewinn" }]
          : []),
      ],
    },
  ]
}

// ============================================================================
// Icons
// ============================================================================

/**
 * OctoTrack Logo Icon
 * Represents an octopus tentacle / shipping anchor hybrid
 */
function LogoIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Ship/anchor design */}
      <path d="M12 2v6" />
      <path d="M12 22v-6" />
      <path d="M4.93 4.93l4.24 4.24" />
      <path d="M14.83 14.83l4.24 4.24" />
      <path d="M2 12h6" />
      <path d="M16 12h6" />
      <path d="M4.93 19.07l4.24-4.24" />
      <path d="M14.83 9.17l4.24-4.24" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  )
}

/**
 * User icon for user menu
 */
function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

/**
 * Logout icon
 */
function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  )
}

/**
 * Settings icon
 */
function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

/**
 * Chevron down icon for dropdowns
 */
function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to manage authentication state
 * Fetches and caches current user data from the API
 */
function useAuth() {
  const [user, setUser] = React.useState<User | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        // Auth cookie is sent automatically by the browser
        const response = await fetch("/api/auth/me")

        if (!response.ok) {
          setUser(null)
          setLoading(false)
          return
        }

        const result = await response.json()

        if (result.success && result.data) {
          setUser({
            id: result.data.id,
            email: result.data.email,
            name: result.data.name,
            isDefaultWorkspaceAdmin: result.data.isDefaultWorkspaceAdmin ?? false,
            isWorkspaceAdmin: result.data.isWorkspaceAdmin ?? false,
          })
        } else {
          setUser(null)
        }
      } catch {
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    fetchUser()
  }, [])

  return { user, loading, setUser }
}

// ============================================================================
// Components
// ============================================================================

/**
 * Navigation dropdown component for links with children
 */
function NavDropdown({
  link,
  isActive,
}: {
  link: NavLink
  isActive: (href: string) => boolean
  pathname: string
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement>(null)

  // Check if any child is active
  const hasActiveChild = link.children?.some(child => isActive(child.href)) ?? false

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1",
          hasActiveChild
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-primary hover:bg-primary/5"
        )}
      >
        {link.label}
        <ChevronDownIcon className={cn(
          "h-4 w-4 transition-transform",
          isOpen && "rotate-180"
        )} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 w-48 rounded-md border bg-card shadow-lg z-50">
          <div className="py-1">
            {link.children?.map((child) => (
              <Link
                key={child.href}
                href={child.href}
                onClick={() => setIsOpen(false)}
                className={cn(
                  "block px-4 py-2 text-sm transition-colors",
                  isActive(child.href)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                )}
              >
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Stale shipment item from the API
 */
interface StaleShipmentItem {
  id: string
  trackingNumber: string
  sealine: string
  sealineName: string
  status: string
  updatedAt: string
}

/**
 * Build fetch URL and headers with workspace/auth context
 */
function useWorkspaceFetch() {
  const buildUrl = React.useCallback((base: string) => {
    const workspaceId = localStorage.getItem("current_workspace_id")
    if (workspaceId) {
      const url = new URL(base, window.location.origin)
      url.searchParams.set("workspaceId", workspaceId)
      return url.toString()
    }
    return base
  }, [])

  const buildHeaders = React.useCallback(() => {
    // Auth cookie is sent automatically by the browser
    const headers: Record<string, string> = {}
    return headers
  }, [])

  return { buildUrl, buildHeaders }
}

/**
 * Hook to fetch stale shipments data
 */
function useStaleShipments(shouldFetch: boolean) {
  const [data, setData] = React.useState<StaleShipmentItem[]>([])
  const [totalStale, setTotalStale] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [hasFetchedCount, setHasFetchedCount] = React.useState(false)
  const { buildUrl, buildHeaders } = useWorkspaceFetch()

  // Fetch count on mount (for badge)
  React.useEffect(() => {
    async function fetchCount() {
      try {
        const response = await fetch(buildUrl("/api/shipments/stale?limit=1"), {
          headers: buildHeaders(),
        })
        if (response.ok) {
          const result = await response.json()
          setTotalStale(result.totalStale)
        }
      } catch {
        // Silently fail for badge count
      } finally {
        setHasFetchedCount(true)
      }
    }
    fetchCount()
  }, [buildUrl, buildHeaders])

  // Fetch full list when menu opens
  React.useEffect(() => {
    if (!shouldFetch) return

    async function fetchStale() {
      setLoading(true)
      try {
        const response = await fetch(buildUrl("/api/shipments/stale"), {
          headers: buildHeaders(),
        })
        if (response.ok) {
          const result = await response.json()
          setData(result.data)
          setTotalStale(result.totalStale)
        }
      } catch {
        // Keep previous state on error
      } finally {
        setLoading(false)
      }
    }
    fetchStale()
  }, [shouldFetch, buildUrl, buildHeaders])

  return { data, totalStale, loading, hasFetchedCount }
}

/**
 * User menu component
 * Shows user name, stale shipments, and logout button
 */
function UserMenu({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [isOpen, setIsOpen] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement>(null)
  const { data: staleShipments, totalStale, loading: staleLoading, hasFetchedCount } = useStaleShipments(isOpen)

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center gap-2"
      >
        <UserIcon className="h-4 w-4" />
        <span className="hidden sm:inline">{user.name}</span>
        {hasFetchedCount && totalStale > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {totalStale > 99 ? "99+" : totalStale}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 rounded-md border bg-card shadow-lg z-50">
          <div className="px-4 py-3 border-b">
            <p className="text-sm font-medium">{user.name}</p>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>

          {/* Stale Shipments Section */}
          <div className="border-b">
            <div className="flex items-center justify-between px-4 py-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Stale Shipments
              </span>
              {totalStale > 0 && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                  {totalStale}
                </span>
              )}
            </div>

            {staleLoading ? (
              <div className="space-y-2 px-4 pb-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-16 animate-pulse rounded bg-muted" />
                  </div>
                ))}
              </div>
            ) : totalStale === 0 ? (
              <div className="px-4 pb-3">
                <p className="text-sm text-green-600 dark:text-green-400">
                  All shipments up to date
                </p>
              </div>
            ) : (
              <>
                <div className="max-h-60 overflow-y-auto px-2 pb-2">
                  {staleShipments.map((shipment) => (
                    <Link
                      key={shipment.id}
                      href={`/shipments/${shipment.id}`}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors"
                    >
                      <div className="min-w-0">
                        <span className="font-mono text-xs font-medium block truncate">
                          {shipment.trackingNumber}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {shipment.sealineName}
                        </span>
                      </div>
                      <span className="ml-2 shrink-0 text-xs font-medium text-red-500 dark:text-red-400">
                        {getFreshnessLabel(shipment.updatedAt)}
                      </span>
                    </Link>
                  ))}
                </div>
                {totalStale > staleShipments.length && (
                  <div className="border-t px-4 py-2">
                    <Link
                      href="/shipments?sortBy=updatedAt&sortOrder=asc"
                      onClick={() => setIsOpen(false)}
                      className="text-xs font-medium text-primary hover:underline"
                    >
                      View all {totalStale} stale shipments
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="py-1">
            {user.isWorkspaceAdmin && (
              <Link
                href="/settings"
                onClick={() => setIsOpen(false)}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-muted transition-colors"
              >
                <SettingsIcon className="h-4 w-4" />
                Settings
              </Link>
            )}
            <div className="border-t my-1" />
            <button
              onClick={() => {
                setIsOpen(false)
                onLogout()
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-muted transition-colors"
            >
              <LogoutIcon className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  /** Whether to show the subtitle */
  showSubtitle?: boolean
}

/**
 * Header - Application navigation header component
 *
 * Provides consistent navigation across all pages with:
 * - OctoTrack logo and branding
 * - Navigation links with active state
 * - Authentication state (login/logout)
 * - Responsive design
 *
 * Usage:
 * ```tsx
 * <Header />
 * // or
 * <Header showSubtitle={false} />
 * ```
 */
const Header = React.forwardRef<HTMLElement, HeaderProps>(
  ({ className, showSubtitle = true, ...props }, ref) => {
    const pathname = usePathname()
    const router = useRouter()
    const { user, loading, setUser } = useAuth()

    /**
     * Check if a navigation link is active
     * Handles both exact matches (Dashboard) and prefix matches (Shipments)
     */
    const isActive = (href: string) => {
      if (href === "/") {
        return pathname === "/"
      }
      return pathname.startsWith(href)
    }

    /**
     * Handle user logout
     */
    const handleLogout = async () => {
      try {
        // Call logout API (clears httpOnly cookie server-side)
        await fetch("/api/auth/logout", {
          method: "POST",
        })

        // Clear user state
        setUser(null)

        // Redirect to login page
        router.push("/auth/login")
      } catch {
        // Even if API call fails, still logout locally
        setUser(null)
        router.push("/auth/login")
      }
    }

    return (
      <header
        ref={ref}
        className={cn("border-b bg-card sticky top-0 z-[1001]", className)}
        {...props}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Title */}
            <Link href="/" className="flex items-center gap-3 group">
              <LogoIcon className="h-8 w-8 text-primary transition-transform group-hover:scale-110" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight">OctoTrack</h1>
                {showSubtitle && (
                  <p className="text-sm text-muted-foreground">
                    Sea Freight Container Tracking
                  </p>
                )}
              </div>
            </Link>

            {/* Navigation and Theme Toggle */}
            <div className="flex items-center gap-4">
              <nav className="flex items-center gap-1 sm:gap-2">
                {buildNavLinks(user?.isDefaultWorkspaceAdmin ?? false).map((link) => (
                  link.children ? (
                    <NavDropdown
                      key={link.href}
                      link={link}
                      isActive={isActive}
                      pathname={pathname}
                    />
                  ) : (
                    <Link
                      key={link.href}
                      href={link.href}
                      className={cn(
                        "px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap",
                        isActive(link.href)
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-primary hover:bg-primary/5"
                      )}
                    >
                      {link.label}
                    </Link>
                  )
                ))}
              </nav>

              {/* Workspace Switcher - Only for logged-in users */}
              {!loading && user && <WorkspaceSwitcher />}

              {/* Auth State - Login Link or User Menu */}
              {!loading && (
                <>
                  {user ? (
                    <UserMenu user={user} onLogout={handleLogout} />
                  ) : (
                    <Button variant="ghost" size="sm" asChild>
                      <Link href="/auth/login">Sign In</Link>
                    </Button>
                  )}
                </>
              )}

              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>
    )
  }
)
Header.displayName = "Header"

export { Header }
